/**
 * PreviewProvider — authenticated, URL-driven preview **phase ladder** +
 * a separate non-numeric "radar" mode.
 *
 * History:
 *   - Pre-2026-06-12 the param was treated as binary (`?preview=1` or
 *     absent).
 *   - 2026-06-12 layered it into TWO tiers (0/1/2) so a recruiter /
 *     engineer could share a deep link that reveals just ONE unfinished
 *     feature (My Imports) without also previewing every other gated
 *     grid.
 *   - 2026-08-03 (this module) replaces the binary tier with a
 *     **numbered, cumulative delivery-phase ladder** (`?preview=1`,
 *     `?preview=2`, extensible via `MAX_PHASE`) plus a flat, un-graduated **`?preview=all`** "radar"
 *     mode:
 *
 *   - `?preview=0` (or absent) — no preview. Only released features.
 *   - `?preview=1` .. `?preview=MAX_PHASE` — reveals the union of
 *     delivery phases `1..N` (see `useFeatureAccess.ts`'s
 *     `PHASE_PAGE_IDS`). Higher phases are cumulative supersets of lower
 *     ones.
 *   - `?preview=all` — a flat "radar" view of literally everything still
 *     on the roadmap (every phase page id + `RADAR_ONLY_PAGE_IDS`),
 *     always balloon'd, used for internal/product review — not part of
 *     the phased rollout narrative. On prod it, like every numbered mode,
 *     skips the ids in `PROD_PREVIEW_BLOCKED_PAGE_IDS`.
 *   - NAMED previews (`NAMED_PREVIEW_MODES`, none today): only the pages
 *     listed for each in `NAMED_PREVIEW_PAGE_IDS`, not cumulative with
 *     anything. `chop-book` (2026-10-07) and `thrive-import` (2026-10-19)
 *     were deleted on 2026-10-20, `four-reports` (2026-10-23) on 2026-10-26,
 *     each with the release of its pages.
 *   - Any other value (`abc`, `true`, a positive integer with no defined
 *     phase e.g. `3`/`4` — retired 2026-10-20 — or `5`/`6` — retired
 *     2026-10-07 — `-1`,
 *     `0`-prefixed junk, …) → treated as absent.
 *   - Duplicate `?preview=1&preview=2` → first-wins per the platform
 *     default of `URLSearchParams.get(...)` (the brief said "last-wins"
 *     but that's a misconception about the API; we follow the platform).
 *
 * On mount it reads `?preview` from the current location:
 *   - valid mode (0, 1, 2, "all", or a named mode) → `sessionStorage[nu_preview_tier] =
 *     String(mode)` (0 clears the key; anything else persists it).
 *   - absent — leave the existing sessionStorage value untouched.
 *
 * Persisting in `sessionStorage` is the load-bearing design choice: a
 * deep link like `/research/imports?preview=1` activates phase 1 on
 * load, and once active it survives internal navigation (which doesn't
 * re-pass the param) for the rest of the browser session.
 *
 * Exposes `previewMode` (the phase integer or `"all"`) + `previewing`
 * (derived bool, back-compat) + an `exitPreview()` affordance via
 * context. Not a security boundary — everything is behind auth and
 * firm-wide-visible by product principle; preview only reveals
 * unfinished UI.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * sessionStorage key. The unsuffixed `nu_preview` key was used by the
 * pre-tier binary implementation; we use the suffixed `nu_preview_tier`
 * so a stale binary "1" can't be misread as the tier integer "1" with
 * different semantics (under the tier model both happened to mean tier
 * 1; the key is kept unchanged across the 2026-08-03 phase-ladder
 * rewrite too — same key, superset of legal string values, no new
 * migration needed).
 */
export const PREVIEW_TIER_STORAGE_KEY = "nu_preview_tier";

/** Legacy key from the binary implementation (kept for cleanup only). */
export const LEGACY_PREVIEW_STORAGE_KEY = "nu_preview";

/**
 * Back-compat re-export. Existing tests imported `PREVIEW_STORAGE_KEY`
 * from this module under the binary model; keep the export name pointed
 * at the new tier key so a test that asserts persistence reads the
 * canonical post-2026-06-12 key.
 */
export const PREVIEW_STORAGE_KEY = PREVIEW_TIER_STORAGE_KEY;

/**
 * The highest defined delivery phase. Bump this (and add the matching
 * `PHASE_PAGE_IDS` entry in `useFeatureAccess.ts` + `phase` fields in
 * `leftMenuModel.ts`) when a new phase is defined. See the PRD's
 * "Extension checklist" in Notes.
 */
export const MAX_PHASE = 2;

/**
 * Named previews: a word, not a rung on the ladder. Each reveals its own
 * pages and nothing else (`NAMED_PREVIEW_PAGE_IDS` in `useFeatureAccess.ts`),
 * on dev and prod alike. `chop-book` (the ChopBook install page and Company
 * Lists) and `thrive-import` (the New Import wizard's Thrive source) were
 * deleted on 2026-10-20, and `four-reports` (the Client Assessment and
 * Backchannel References in the Candidates tab, epic
 * 2026-10-22-candidate-reports-four-kinds D5) on 2026-10-26, each when their
 * pages were released, so a stale link reads as absent.
 *
 * `staff-members` (2026-10-27, stt-firm-roster): the Administration console
 * (people, activity, admins pages). Gated here — not in the numeric ladder or
 * radar — so internal QA can reach it without exposing it to all preview users.
 */
export const NAMED_PREVIEW_MODES = ["staff-members"] as const;
export type NamedPreviewMode = (typeof NAMED_PREVIEW_MODES)[number];

/** `5` (Bullseye) and `6` (Candidates) existed 2026-10-06..07, `3` (Company
 *  Lists) and `4` (ChopBook) until 2026-10-20; each left with its release, and
 *  a stale `?preview=3`..`6` link reads as absent. */
export type PreviewMode = 0 | 1 | 2 | "all" | NamedPreviewMode;

export function isNamedPreviewMode(mode: PreviewMode): mode is NamedPreviewMode {
  return (NAMED_PREVIEW_MODES as readonly PreviewMode[]).includes(mode);
}

interface PreviewContextValue {
  /**
   * The active preview mode: `0` (no preview), a numbered phase
   * (`1..MAX_PHASE`), or `"all"` (the flat radar view).
   */
  previewMode: PreviewMode;
  /** Derived: `previewMode !== 0`. Kept for back-compat with code that
   * doesn't care which mode. */
  previewing: boolean;
  /** Drop the persisted mode + the `?preview` URL param. */
  exitPreview: () => void;
}

const PreviewContext = createContext<PreviewContextValue | null>(null);

/**
 * Parse a raw param value into a valid mode. Anything other than
 * `"0"`, `"1"`..`"<MAX_PHASE>"`, or `"all"` → null (caller decides what
 * to do with it). Case-sensitive, matching the pre-existing exact-string
 * style (no `.toLowerCase()` coercion).
 */
function parseMode(raw: string | null): PreviewMode | null {
  if (raw === "all") return "all";
  if (raw === null) return null;
  const named = NAMED_PREVIEW_MODES.find((m) => m === raw);
  if (named) return named;
  // Reject non-canonical numeric strings (leading zeros, signs, etc.) —
  // only the exact decimal digit strings "0".."<MAX_PHASE>" are valid,
  // matching the pre-existing exact-string parsing style.
  for (let n = 0; n <= MAX_PHASE; n++) {
    if (raw === String(n)) return n as PreviewMode;
  }
  return null;
}

function readPersistedMode(): PreviewMode {
  const stored = sessionStorage.getItem(PREVIEW_TIER_STORAGE_KEY);
  const parsed = parseMode(stored);
  // A stored value that is no mode any more — a retired named preview such as
  // `four-reports`, or a retired rung — is dropped, not just ignored: left in
  // place it would come back to life the day a mode reused the word
  // (/pr-review #1092 iter 1).
  if (stored !== null && parsed === null) sessionStorage.removeItem(PREVIEW_TIER_STORAGE_KEY);
  return parsed ?? 0;
}

function readInitialMode(): PreviewMode {
  // Cleanup: drop the legacy `nu_preview` key on every mount. If a tab
  // was already in preview under the binary model when this code ships,
  // we don't silently inherit "tier 2 = everything" — the recruiter
  // re-clicks their deep link, gets a stable mode, and moves on. The
  // unconditional removal is cheap (idempotent no-op if absent) and the
  // alternative (auto-migrating legacy=1 → tier=2) would lock a user
  // into "everything revealed" semantics they didn't deep-link into.
  sessionStorage.removeItem(LEGACY_PREVIEW_STORAGE_KEY);

  // Read the URL param first; it overrides any persisted value so a
  // `?preview=0` link can explicitly turn preview off, and `?preview=N`
  // / `?preview=all` can re-key the persisted mode across sessions.
  const params = new URLSearchParams(window.location.search);
  // `URLSearchParams.get` returns the FIRST value if the param is
  // repeated — that's the platform default. The brief said "last-wins"
  // but that's a misconception; we follow the platform.
  const raw = params.get("preview");
  if (raw === null) {
    return readPersistedMode();
  }
  const parsed = parseMode(raw);
  if (parsed === null) {
    // Invalid value (e.g. ?preview=abc, ?preview=true, ?preview=5 since
    // Phase 5 was retired). Treated as absent — DO NOT touch
    // sessionStorage so a returning tab keeps whatever mode it already
    // had.
    return readPersistedMode();
  }
  if (parsed === 0) {
    sessionStorage.removeItem(PREVIEW_TIER_STORAGE_KEY);
  } else {
    sessionStorage.setItem(PREVIEW_TIER_STORAGE_KEY, String(parsed));
  }
  return parsed;
}

export function PreviewProvider({ children }: { children: ReactNode }) {
  // Initialise once from URL → sessionStorage. Lazy initialiser so the
  // read happens exactly once per provider mount.
  const [previewMode, setPreviewMode] = useState<PreviewMode>(readInitialMode);

  const exitPreview = useCallback(() => {
    sessionStorage.removeItem(PREVIEW_TIER_STORAGE_KEY);
    // Drop the ?preview param from the URL without a full reload so the
    // shared link doesn't re-activate preview on the next navigation.
    const url = new URL(window.location.href);
    url.searchParams.delete("preview");
    window.history.replaceState({}, "", url.pathname + url.search + url.hash);
    setPreviewMode(0);
  }, []);

  const value = useMemo<PreviewContextValue>(
    () => ({
      previewMode,
      previewing: previewMode !== 0,
      exitPreview,
    }),
    [previewMode, exitPreview],
  );

  return (
    <PreviewContext.Provider value={value}>{children}</PreviewContext.Provider>
  );
}

export function usePreview(): PreviewContextValue {
  const ctx = useContext(PreviewContext);
  if (!ctx) {
    throw new Error("usePreview must be used within a <PreviewProvider>");
  }
  return ctx;
}

/**
 * The active preview mode, or `0` (no preview) when no `<PreviewProvider>` is
 * mounted — instead of throwing like `usePreview`.
 *
 * For a gate inside a component that the app always renders under the root
 * provider (`main.tsx`) but that dozens of isolated test harnesses mount
 * without one: `ImportDetailPage` gates the person panel's avatar button on
 * `pageVisible(...)` with this mode, the SAME resolution `useFeatureAccess`
 * and `FeatureRoute` use, without forcing a provider into every suite. In the
 * app the two hooks return the same mode; outside a provider "no preview" is
 * the only honest answer. Not for new routes — those go through
 * `FeatureRoute`.
 */
export function usePreviewModeIfMounted(): PreviewMode {
  return useContext(PreviewContext)?.previewMode ?? 0;
}
